/**
 * 管理工具栏状态的单例类
 */
import { IEventEmitter } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';
import { EventEmitter as EventBus } from '@src/slide/infrastructure/module/independent/event-emitter';
import axios from 'axios';
import { ToolbarType } from '../../../../../js/constant';
import { RUNTIME } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { observable } from 'mobx';
import { waitWorkbenchMountHandler } from '@utils/workbench';
import { IToolbarStateManager } from './toolbar-state-manager.interface';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

const defaultMode = ToolbarType.DOUBLE_TOOLBAR;
export const TOOLBAR_TYPE_KEY = 'toolbar_mode_slide';
// 新版工具栏是否已经在该客户端展示过
export const HAS_NEW_TOOLBAR_SHOWED = 'has_new_toolbar_showed';


export enum ToolBarStateEventsKeys {
    SWITCH_TOOLBAR_MODE_EVENT  = 'swithToolbarMode',
}
export interface ToolBarStateEventsMap {
    [ToolBarStateEventsKeys.SWITCH_TOOLBAR_MODE_EVENT]: string;
}

const docsBaseUrl = RUNTIME.API_ROOT;
// 设置用户属性接口
// 请求参数格式
// {"userAttribute":"{\"attributes\":[{\"name\":\"属性名称\",\"value\":\"属性值\"}]}"}
// 回包：（code不为0是异常）
// {"header":{"code":0,"message":"success","seq":""}}
const CGI_UPDATE_USER_ATTRIBUTE = `${docsBaseUrl}v2/user/attribute/update`;

export class ToolbarStateManager implements IToolbarStateManager {
    @observable
    public isClickOnToolbar = false;
    /**
     * header是否折叠，默认为false
     */
    @observable
    public isHeaderFold = false;
    /**
     * header(指顶部栏)是否正在动画
     */
    @observable
    public isToolbarAniming = false;
    /**
     * 工具栏菜单是否展示
     */
    @observable
    public isToolbarMenuShown = false;
    public lastInsertButtonClick = 0;

    /**
     * 移动端工具栏是否触发了行为
     */
    @observable
    public isMobileToolbarClick = false;

    /**
     * header的类型
     */
    @observable
    private currMode = '';
    private event: IEventEmitter<ToolBarStateEventsMap>;

    public constructor() {
        this.event = new EventBus();
        this.currMode = this.getDefaultToolbarMode();
        // 等待 workbench 挂载
        waitWorkbenchMountHandler(() => {
            this.updateToolbarMode(this.currMode);
        });
    }

    public getToolbarMode = (): string => this.currMode;

    public setToolbarMode = (mode: string) => {
        this.currMode = mode;
        userLocalStorage.setItem(LocalStorageKeys.TOOLBAR_MODE_SLIDE, mode);
        this.setUserToolbarModeSetting(mode);
    };

    /**
     * 事件派发更新工具栏 ui
     * @param toolbarMode 默认是双栏
     */
    private updateToolbarMode = (toolbarMode = defaultMode) => {
        this.sendEvent(toolbarMode);
    };

    private setUserToolbarModeSetting = (newMode: string) => {
        const param = {
            userAttribute: JSON.stringify({
                attributes: [
                    {
                        name: TOOLBAR_TYPE_KEY,
                        value: newMode,
                    },
                ],
            }),
        };
        axios(CGI_UPDATE_USER_ATTRIBUTE, {
            method: 'POST',
            data: param,
        }).then((res) => {
            const { data } = res;
            const { header } = data;
            if (header?.code !== 0) {
                window.log.error('[toolbarStateManager]setUserToolbarModeSetting failed', header.code, header.message);
            }
        })
            .catch((data) => {
                window.log.error('[toolbarStateManager]setUserToolbarModeSetting error', data);
            });
    };

    /**
     * 初始化ToolbarMode
     * 服务端返回配置信息 > 本地存储 > 默认
     */
    private getDefaultToolbarMode() {
        // 工具栏改版后，首次打开ppt，默认打开双栏，并更新缓存数据
        if (userLocalStorage.getItem(LocalStorageKeys.HAS_NEW_TOOLBAR_SHOWED) === null) {
            userLocalStorage.setItem(LocalStorageKeys.HAS_NEW_TOOLBAR_SHOWED, true);
            this.setToolbarMode(defaultMode);
            return defaultMode;
        }
        // 从basicClientVars里拿到用户的工具栏配置数据
        const clientVarsToolbarMode = window?.basicClientVars?.userInfo?.settings?.toolbarMode;
        return clientVarsToolbarMode || userLocalStorage.getItem(LocalStorageKeys.TOOLBAR_MODE_SLIDE, defaultMode);
    }

    private sendEvent(serverToolbarMode: string) {
        const allModeArr = [ToolbarType.SINGLE_TOOLBAR, ToolbarType.DOUBLE_TOOLBAR];
        if (allModeArr.includes(serverToolbarMode) && serverToolbarMode !== this.currMode) {
            // send toolbarMode update event
            this.event.emit(ToolBarStateEventsKeys.SWITCH_TOOLBAR_MODE_EVENT, serverToolbarMode);
        }
    }
}
